import { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Link } from "react-router-dom";

const inp = {
  width: "100%",
  padding: "10px 14px",
  backgroundColor: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "10px",
  color: "#E7EBEF",
  fontSize: "14px",
  outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
  WebkitBoxShadow: "0 0 0 1000px rgba(13,17,23,0.95) inset",
  WebkitTextFillColor: "#E7EBEF",
};

const inpFocus = {
  borderColor: "#30A69C",
  boxShadow: "0 0 0 2px rgba(48,166,156,0.2)",
};

const lbl = {
  display: "block",
  fontSize: "12px",
  fontWeight: 500,
  color: "#94A3B8",
  marginBottom: "6px",
  letterSpacing: "0.03em",
  textTransform: "uppercase",
};

const EyeIcon = ({ visible }) =>
  visible ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

const TextField = ({ id, name, type = "text", placeholder, required, value, onChange }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      id={id}
      name={name}
      type={type}
      required={required}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      style={{ ...inp, ...(focused ? inpFocus : {}) }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const PasswordField = ({ id, name, placeholder, value, onChange }) => {
  const [show, setShow] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        name={name}
        type={show ? "text" : "password"}
        required
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{ ...inp, paddingRight: "44px", ...(focused ? inpFocus : {}) }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors duration-200"
        style={{ color: "#64748B" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#94A3B8")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#64748B")}
      >
        <EyeIcon visible={show} />
      </button>
    </div>
  );
};

const SignupInputFields = ({ formData, handleChange, onSubmit, isFetching, error, handleGoogleSignIn }) => {
  const { firstName, lastName, department, institutionName, number, email, password, confirmPassword } = formData;

  const formatName = (name) =>
    name.split(" ").map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ");

  return (
    <div className="space-y-4">
      <form className="space-y-4" onSubmit={onSubmit}>
        {/* First / Last Name */}
        <div className="grid grid-cols-2 sm:grid-cols-1 gap-4">
          <div>
            <label htmlFor="firstName" style={lbl}>First Name (*)</label>
            <TextField id="firstName" name="firstName" placeholder="Dr. First" required
              value={firstName} onChange={(e) => handleChange("firstName")(formatName(e.target.value))} />
          </div>
          <div>
            <label htmlFor="lastName" style={lbl}>Last Name (*)</label>
            <TextField id="lastName" name="lastName" placeholder="Last" required
              value={lastName} onChange={(e) => handleChange("lastName")(formatName(e.target.value))} />
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" style={lbl}>Email (*)</label>
          <TextField id="email" name="email" type="email" placeholder="doctor@hospital.com" required
            value={email} onChange={(e) => handleChange("email")(e.target.value)} />
        </div>

        {/* Institution */}
        <div>
          <label htmlFor="institutionName" style={lbl}>Institution Name</label>
          <TextField id="institutionName" name="institutionName" placeholder="Hospital / Clinic name"
            value={institutionName} onChange={(e) => handleChange("institutionName")(e.target.value)} />
        </div>

        {/* Department */}
        <div>
          <label htmlFor="department" style={lbl}>Department</label>
          <TextField id="department" name="department" placeholder="e.g. Cardiology"
            value={department} onChange={(e) => handleChange("department")(e.target.value)} />
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="number" style={lbl}>Phone Number</label>
          <PhoneInput
            value={number}
            onChange={handleChange("number")}
            inputStyle={{
              width: "100%",
              height: "42px",
              backgroundColor: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "10px",
              fontSize: "14px",
              paddingLeft: "52px",
              color: "#E7EBEF",
              fontFamily: "Inter, sans-serif",
            }}
            buttonStyle={{
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "10px 0 0 10px",
              backgroundColor: "rgba(255,255,255,0.05)",
            }}
            dropdownStyle={{
              backgroundColor: "#13171F",
              color: "#E7EBEF",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
            containerStyle={{ width: "100%" }}
            placeholder="Enter your phone number"
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" style={lbl}>Password (*)</label>
          <PasswordField id="password" name="password" placeholder="••••••••"
            value={password} onChange={(e) => handleChange("password")(e.target.value)} />
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" style={lbl}>Confirm Password (*)</label>
          <PasswordField id="confirmPassword" name="confirmPassword" placeholder="••••••••"
            value={confirmPassword} onChange={(e) => handleChange("confirmPassword")(e.target.value)} />
        </div>

        <div className="text-xs" style={{ color: "#64748B" }}>(*) Required fields</div>

        {/* Error */}
        {error && (
          <div className="rounded-lg p-3" style={{ backgroundColor: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)" }}>
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isFetching}
          className="w-full flex items-center justify-center font-semibold disabled:opacity-50 rounded-xl py-3 px-4 transition-all duration-200"
          style={{ backgroundColor: "#30A69C", color: "#fff", border: "none" }}
          onMouseEnter={(e) => { if (!isFetching) e.currentTarget.style.backgroundColor = "#228880"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#30A69C"; }}
        >
          {isFetching ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Signing up...
            </>
          ) : "Sign Up"}
        </button>
      </form>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div style={{ flex: 1, height: "1px", backgroundColor: "rgba(255,255,255,0.1)" }} />
        <span className="text-xs" style={{ color: "#64748B" }}>or</span>
        <div style={{ flex: 1, height: "1px", backgroundColor: "rgba(255,255,255,0.1)" }} />
      </div>

      {/* Google */}
      <button
        onClick={handleGoogleSignIn}
        disabled={isFetching}
        className="w-full flex items-center justify-center font-medium disabled:opacity-50 rounded-xl py-3 px-4 transition-all duration-200 space-x-3"
        style={{ backgroundColor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", color: "#E7EBEF" }}
        onMouseEnter={(e) => { if (!isFetching) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.09)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)"; }}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        <span>Continue with Google</span>
      </button>

      {/* Login link */}
      <div className="text-center flex items-center justify-center space-x-2 mt-2">
        <p className="text-sm" style={{ color: "#64748B" }}>Already have an account?</p>
        <Link to="/login" className="font-semibold text-sm transition-colors duration-200"
          style={{ color: "#30A69C", textDecoration: "none" }}
          onMouseOver={(e) => (e.currentTarget.style.color = "#50C6BC")}
          onMouseOut={(e) => (e.currentTarget.style.color = "#30A69C")}
        >
          Log In
        </Link>
      </div>
    </div>
  );
};

export default SignupInputFields;
